/**
 * Pure Instagram Graph media mapping shared by the browser and Edge runtime.
 * UI boundaries carry Mora `connectionId`; provider account identifiers never
 * leave the server-owned token/connection layer.
 */

export interface InstagramGraphMediaChild {
  id?: string;
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
}

export interface InstagramGraphMediaItem {
  id: string;
  media_type?: string;
  media_product_type?: string;
  is_shared_to_feed?: boolean;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  caption?: string;
  timestamp: string;
  children?: { data?: InstagramGraphMediaChild[] };
}

export type InstagramFeedMediaType = 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
export type InstagramProfilePlacement = 'feed' | 'reel';

export interface NativeProfileItemInsert {
  connection_id: string;
  native_post_id: string;
  placement: InstagramProfilePlacement;
  media_type: InstagramFeedMediaType;
  share_to_feed: boolean;
  thumbnail_path: string;
  source_media_url: string | null;
  permalink: string | null;
  caption_excerpt: string | null;
  published_at: string;
  observed_at: string;
  sync_generation: number;
  tombstoned_at: null;
  provider_metadata: Record<string, unknown>;
}

export interface NativeStoryItemInsert {
  connection_id: string;
  native_story_id: string;
  media_type: 'IMAGE' | 'VIDEO';
  thumbnail_path: string;
  source_media_url: string | null;
  permalink: string | null;
  published_at: string;
  expires_at: string;
  observed_at: string;
  sync_generation: number;
  provider_metadata: Record<string, unknown>;
}

const CAPTION_EXCERPT_MAX = 240;

function normalizeMediaType(value: string | undefined): InstagramFeedMediaType {
  if (value === 'VIDEO' || value === 'CAROUSEL_ALBUM') return value;
  return 'IMAGE';
}

function normalizeStoryMediaType(value: string | undefined): 'IMAGE' | 'VIDEO' {
  return value === 'VIDEO' ? 'VIDEO' : 'IMAGE';
}

export function resolveInstagramProfilePlacement(
  mediaProductType: string | undefined,
): InstagramProfilePlacement | 'story' {
  if (mediaProductType === 'REELS') return 'reel';
  if (mediaProductType === 'STORY') return 'story';
  return 'feed';
}

/** Returns a renderable image source. Video without a provider thumbnail is skipped. */
export function resolveInstagramPreviewSource(item: InstagramGraphMediaItem): string | null {
  if (item.thumbnail_url) return item.thumbnail_url;
  if (item.media_type === 'VIDEO') return null;
  if (item.media_url) return item.media_url;

  const firstChild = item.children?.data?.[0];
  if (firstChild?.thumbnail_url) return firstChild.thumbnail_url;
  if (firstChild?.media_type === 'VIDEO') return null;
  return firstChild?.media_url ?? null;
}

function captionExcerpt(caption: string | undefined): string | null {
  const value = caption?.trim();
  return value ? value.slice(0, CAPTION_EXCERPT_MAX) : null;
}

function safeProviderMetadata(item: InstagramGraphMediaItem): Record<string, unknown> {
  return {
    mediaProductType: item.media_product_type ?? null,
    childCount: item.children?.data?.length ?? 0,
  };
}

export function mapInstagramMediaToNativeProfileItem(
  item: InstagramGraphMediaItem,
  connectionId: string,
  thumbnailPath: string,
  syncGeneration: number,
  observedAt: string = new Date().toISOString(),
): NativeProfileItemInsert | null {
  const placement = resolveInstagramProfilePlacement(item.media_product_type);
  if (placement === 'story') return null;

  return {
    connection_id: connectionId,
    native_post_id: item.id,
    placement,
    media_type: normalizeMediaType(item.media_type),
    // Instagram's Reel visibility flag is truth-bearing for the profile grid.
    // If the provider omits it, fail closed instead of asserting that the Reel
    // appears on the profile.
    share_to_feed: item.media_product_type === 'REELS' ? item.is_shared_to_feed === true : true,
    thumbnail_path: thumbnailPath,
    source_media_url: resolveInstagramPreviewSource(item),
    permalink: item.permalink ?? null,
    caption_excerpt: captionExcerpt(item.caption),
    published_at: item.timestamp,
    observed_at: observedAt,
    sync_generation: syncGeneration,
    tombstoned_at: null,
    provider_metadata: safeProviderMetadata(item),
  };
}

export function mapInstagramMediaToNativeStoryItem(
  item: InstagramGraphMediaItem,
  connectionId: string,
  thumbnailPath: string,
  syncGeneration: number,
  observedAt: string = new Date().toISOString(),
): NativeStoryItemInsert {
  const publishedAt = new Date(item.timestamp);
  const expiresAt = new Date(publishedAt.getTime() + 24 * 60 * 60 * 1000);

  return {
    connection_id: connectionId,
    native_story_id: item.id,
    media_type: normalizeStoryMediaType(item.media_type),
    thumbnail_path: thumbnailPath,
    source_media_url: resolveInstagramPreviewSource(item),
    permalink: item.permalink ?? null,
    published_at: publishedAt.toISOString(),
    expires_at: expiresAt.toISOString(),
    observed_at: observedAt,
    sync_generation: syncGeneration,
    provider_metadata: safeProviderMetadata(item),
  };
}

export interface PublishedFeedItem {
  id: string;
  connectionId: string;
  kind: 'published';
  placement: InstagramProfilePlacement;
  mediaUrl: string;
  permalink: string | null;
  mediaType: InstagramFeedMediaType;
  publishedAt: string;
  sourcedAt: string;
}

export interface NativeProfileItemRow {
  connection_id: string;
  native_post_id: string;
  placement: string;
  thumbnail_path: string;
  permalink: string | null;
  media_type: string;
  published_at: string;
  observed_at: string;
}

export function mapNativeProfileRowToPublishedFeedItem(
  row: NativeProfileItemRow,
  durableMediaUrl: string,
): PublishedFeedItem {
  return {
    id: row.native_post_id,
    connectionId: row.connection_id,
    kind: 'published',
    placement: row.placement === 'reel' ? 'reel' : 'feed',
    mediaUrl: durableMediaUrl,
    permalink: row.permalink,
    mediaType: normalizeMediaType(row.media_type),
    publishedAt: row.published_at,
    sourcedAt: row.observed_at,
  };
}
